import { useCallback, useRef } from 'react';
import usePolling from '../../../hooks/usePolling';
import { GetLocationRoutes, GetStagedNextmvRun } from '../../../libs/GraphQL';
import { formatDateForApi } from '../utils';
import { RECEIPT_STATUS } from './NextmvReceiptStrip';

// Polls while there's in-flight reroute work. Completion is detected off the
// staged run, NOT off a `currentRunId` field on locationRoutes:
//
//   getStagedNextmvRun(date, fc) returns the latest SUCCEEDED run for the FC.
//   Submitting a reroute creates a new run (receipt.runId) with the current
//   run as its previousRunId. While that run is RUNNING/FAILED the query keeps
//   returning the previous run; once it SUCCEEDS the query returns the new run.
//   So a PROCESSING receipt confirms exactly when
//   getStagedNextmvRun.nextmvRunId === receipt.runId.
//
// Because RUNNING and FAILED both leave the query on the previous run, success
// is observable but failure is not — a receipt that never flips is stuck. A
// per-receipt deadline (`deadlineTicks`) therefore flips a receipt to FAILED
// after N ticks so the card can never lock forever. Empty-runId receipts (a
// no-op/ghost-only submit) can never match and are caught by the same deadline.
//
// Each tick also refetches locationRoutes to keep the displayed stop sequence
// fresh. Side-effect only — no state owned here; the container supplies the
// receipt/syncing mutators from useReceiptsAndSyncing.

const DRAFT_KEY_PREFIX = 'route-ops:route-edits:';

function routeNamesOf(receipt) {
  return receipt.routeName
    .split(',')
    .map((n) => n.trim())
    .filter(Boolean);
}

export default function useConfirmReceiptsPoller({
  enabled,
  activeDate,
  fulfillmentCenter,
  receipts,
  syncingRoutes,
  patchReceipt,
  removeFromSyncing,
  onRoutesFetched,
  onConfirmed,
  intervalMs = 30000,
  deadlineTicks = 10,
}) {
  // receiptId -> number of ticks it has been observed still PROCESSING.
  const tickCountRef = useRef(new Map());

  const tick = useCallback(async () => {
    if (!enabled) return;
    const syncingAtFetchStart = new Set(syncingRoutes);
    const date = formatDateForApi(activeDate);

    // Refresh the displayed stops (best-effort — a failure here must not stop
    // the completion/deadline logic below).
    try {
      const resp = await GetLocationRoutes({
        stockingDate: date,
        calculateTotes: true,
      });
      onRoutesFetched(resp?.routes || []);
    } catch (e) {
      // eslint-disable-next-line no-console -- surface the refetch failure
      // without aborting completion detection.
      console.error('Route refetch failed during sync poll:', e);
    }

    // The completion signal: the latest SUCCEEDED run id for this (date, fc).
    let latestRunId = null;
    if (fulfillmentCenter) {
      try {
        const staged = await GetStagedNextmvRun({
          dropOffDate: date,
          fulfillmentCenter,
        });
        latestRunId = staged?.nextmvRunId || null;
      } catch (e) {
        // No SUCCEEDED run yet (or query error) — leave latestRunId null so
        // nothing confirms this tick; the deadline still advances.
        latestRunId = null;
      }
    }

    const counts = tickCountRef.current;
    const confirmedRouteNames = new Set();
    const timedOutRouteNames = new Set();
    const liveIds = new Set();
    let confirmedAny = false;

    receipts.forEach((r) => {
      if (r.status !== RECEIPT_STATUS.PROCESSING) return;
      liveIds.add(r.id);

      if (r.runId && latestRunId === r.runId) {
        patchReceipt(r.id, { status: RECEIPT_STATUS.CONFIRMED });
        routeNamesOf(r).forEach((name) => confirmedRouteNames.add(name));
        counts.delete(r.id);
        confirmedAny = true;
        return;
      }

      const nextCount = (counts.get(r.id) || 0) + 1;
      counts.set(r.id, nextCount);
      if (nextCount >= deadlineTicks) {
        patchReceipt(r.id, {
          status: RECEIPT_STATUS.FAILED,
          errorMessage:
            'Timed out waiting for the run to finish — it may still be processing. Your edits are kept; review and retry.',
        });
        routeNamesOf(r).forEach((name) => timedOutRouteNames.add(name));
        counts.delete(r.id);
      }
    });

    // Forget receipts that are no longer PROCESSING (dismissed/confirmed/failed
    // elsewhere) so the map can't grow unbounded.
    [...counts.keys()].forEach((id) => {
      if (!liveIds.has(id)) counts.delete(id);
    });

    // Drop persisted drafts for confirmed routes (a confirmed reroute means the
    // edits are applied server-side). Timed-out routes keep their draft so the
    // supervisor can retry.
    confirmedRouteNames.forEach((routeName) => {
      try {
        window.localStorage.removeItem(
          `${DRAFT_KEY_PREFIX}${date}:${routeName}`,
        );
      } catch (e) {
        /* ignore */
      }
    });

    // Unlock routes that resolved this tick and were syncing before the fetch.
    const resolved = new Set([...confirmedRouteNames, ...timedOutRouteNames]);
    const toRemove = [...syncingAtFetchStart].filter((name) =>
      resolved.has(name),
    );
    removeFromSyncing(toRemove);

    // A confirmation advances the staged run to the new run id; let the
    // container refetch it so the next reroute uses the fresh previousRunId.
    if (confirmedAny && onConfirmed) onConfirmed();
  }, [
    enabled,
    activeDate,
    fulfillmentCenter,
    receipts,
    syncingRoutes,
    patchReceipt,
    removeFromSyncing,
    onRoutesFetched,
    onConfirmed,
    deadlineTicks,
  ]);

  usePolling(tick, { intervalMs, enabled });
}
